import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';
import { useAuthContext } from './useAuthContext';
import {
  sendOrderConfirmation,
  sendNewOrderNotificationToStore,
  getResolvedEmailApiUrl,
} from '../services/emailService';
import { formatOrderReference } from '../utils/orderReference';

const getShipmentStatusStep = (shipmentStatusName) => {
  if (!shipmentStatusName) return 0;
  const status = String(shipmentStatusName).toLowerCase().trim();

  if (status === 'shipping request' || status === 'طلب شحن' || status.includes('shipping request')) return 1;
  if (
    status === 'received in the store' ||
    status === 'تم الاستلام بالمخزن' ||
    status.includes('received') ||
    status.includes('الاستلام') ||
    status.includes('المخزن')
  ) return 2;
  if (
    status === 'out to deliver' ||
    status === 'جاري التوصيل' ||
    status.includes('out to deliver') ||
    status.includes('out for delivery') ||
    status.includes('جاري') ||
    status.includes('التوصيل')
  ) return 3;
  if (
    status === 'delivered to consignee' ||
    status === 'تم التسليم' ||
    status === 'تم التسليم للمستلم' ||
    status.includes('delivered') ||
    status.includes('تم التسليم')
  ) return 4;
  return 0;
};

const canonicalShipmentStatus = (step, fallbackStatusName) => {
  if (step === 1) return 'Shipping Request';
  if (step === 2) return 'Received in the store';
  if (step === 3) return 'Out To Deliver';
  if (step === 4) return 'Delivered To Consignee';
  return fallbackStatusName || null;
};

const mapOrderWithShipmentMeta = (order) => {
  const items = Array.isArray(order?.order_items) ? order.order_items : [];
  let maxStep = 0;
  let firstStatusName = null;
  let trackingUrl = null;

  items.forEach((item) => {
    if (!firstStatusName && item?.shipment_status_name) {
      firstStatusName = item.shipment_status_name;
    }
    if (!trackingUrl && item?.tracking_url) {
      trackingUrl = item.tracking_url;
    }
    const step = getShipmentStatusStep(item?.shipment_status_name);
    if (step > maxStep) maxStep = step;
  });

  return {
    ...order,
    shipment_step: maxStep,
    shipment_status_name: canonicalShipmentStatus(maxStep, firstStatusName),
    tracking_url: trackingUrl || null,
  };
};

export const useOrders = () => {
  const { session } = useAuthContext();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!session) {
      setOrders([]);
      setLoading(false);
      return;
    }

    const fetchOrders = async () => {
      try {
        setLoading(true);
        console.log('Fetching orders for user:', session.user.id);

        // Fetch orders with order items and product data
        const { data, error: fetchError } = await supabase
          .from('orders')
          .select(`
            *,
            order_items:order_items(
              *,
              note,
              product:products(id, name, img, images)
            )
          `)
          .eq('profile_id', session.user.id)
          .eq('payment_status', 'paid')
          .order('created_at', { ascending: false });

        console.log('Orders response - data:', data, 'error:', fetchError);

        if (fetchError) {
          console.error('Error fetching orders:', fetchError);
          throw fetchError;
        }

        if (data) {
          console.log(`Found ${data.length} orders:`, data);
          setOrders(data.map(mapOrderWithShipmentMeta));
        } else {
          console.log('No orders found');
          setOrders([]);
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError(err.message);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [session]);

  const createOrderFromCart = async (cartId, shippingInfoId, shippingFee = 0, discount = 0, tax = 0, onlinePaymentFee = 0, currency = 'OMR') => {
    if (!session) {
      throw new Error('You must be logged in to place orders');
    }

    try {
      console.log('Creating order from cart:', cartId, 'shipping:', shippingInfoId);
      console.log('Shipping ID type:', typeof shippingInfoId);

      // Step 1: Get cart data
      console.log('Step 1: Fetching cart data...');
      const { data: cartData, error: cartError } = await supabase.rpc('cart_get');
      if (cartError) {
        console.error('Cart fetch error:', cartError);
        throw cartError;
      }

      const cart = cartData?.cart;
      const cartItems = cartData?.items || [];

      console.log('Cart data:', cart);
      console.log('Cart items count:', cartItems.length);
      console.log('Full cart response:', JSON.stringify(cartData, null, 2));
      if (cartItems.length > 0) {
        console.log('First cart item structure:', cartItems[0]);
        console.log('First cart item keys:', Object.keys(cartItems[0]));
        console.log('First cart item variant_id:', cartItems[0].variant_id);
        console.log('All cart items variant_ids:', cartItems.map(item => ({ id: item.id, variant_id: item.variant_id })));
      }

      if (!cart || cartItems.length === 0) {
        throw new Error('Cart is empty or not found');
      }

      // Step 2: Get shipping information
      console.log('Step 2: Fetching shipping information...');
      
      // Convert shippingInfoId to string to ensure it's treated as UUID
      const shippingId = String(shippingInfoId);
      console.log('Converted shipping ID:', shippingId);

      const { data: shippingData, error: shippingError } = await supabase
        .from('shipping_information')
        .select('*')
        .eq('id', shippingId)
        .eq('profile_id', session.user.id)
        .single();

      console.log('Shipping query result:', { shippingData, shippingError });

      if (shippingError) {
        console.error('Shipping error details:', shippingError);
        throw new Error(`Shipping address error: ${shippingError.message}`);
      }

      if (!shippingData) {
        throw new Error('Shipping address not found');
      }

      console.log('Shipping data found:', shippingData);

      // Always use account/profile email in order snapshot (not optional address email)
      let profileEmail = session.user?.email || null;
      try {
        const { data: rpcEmail, error: rpcErr } = await supabase.rpc('get_user_email', {
          user_id: session.user.id
        });
        if (!rpcErr && typeof rpcEmail === 'string' && rpcEmail.includes('@')) {
          profileEmail = rpcEmail.trim();
        }
      } catch (_e) {
        // fallback to auth session email
      }
      const shippingSnapshot = {
        ...shippingData,
        email: profileEmail || null,
      };

      // Step 3: Create order
      console.log('Step 3: Creating order...');
      
      // Calculate subtotal from cart items (prices already have discounts applied)
      // This ensures we use the actual item prices, not the potentially outdated cart.total
      const calculatedSubtotal = cartItems.reduce((sum, item) => {
        const price = parseFloat(item.price || 0);
        const quantity = parseInt(item.quantity || 1);
        return sum + (price * quantity);
      }, 0);
      
      console.log('💰 Order calculation:');
      console.log('  Cart items count:', cartItems.length);
      console.log('  Calculated subtotal from items:', calculatedSubtotal.toFixed(2), 'OMR');
      console.log('  Cart.total (from DB):', cart.total, 'OMR');
      console.log('  Shipping fee:', shippingFee.toFixed(3), 'OMR');
      console.log('  Discount:', discount.toFixed(2), 'OMR');
      console.log('  Online payment fee:', onlinePaymentFee.toFixed(3), 'OMR');
      
      // Add online payment fee to tax_total
      const totalTax = Number(tax) + Number(onlinePaymentFee);
      // Calculate grand total using calculated subtotal (not cart.total)
      const grandTotal = Number(calculatedSubtotal) + Number(shippingFee) - Number(discount) + totalTax;
      
      console.log('  Grand total:', grandTotal.toFixed(2), 'OMR');
      
      const orderData = {
        profile_id: session.user.id,
        cart_id: cart.id, // Include cart_id after fixing column type to bigint
        currency: String(currency || 'OMR').toUpperCase(),
        subtotal: Number(calculatedSubtotal), // Use calculated subtotal, not cart.total
        shipping_fee: Number(shippingFee),
        discount_total: Number(discount),
        tax_total: totalTax,
        grand_total: grandTotal,
        status: 'pending',
        payment_status: 'unpaid',
        fulfillment_status: 'unfulfilled',
        shipping_snapshot: shippingSnapshot,
        shipping_information_id: shippingId
      };

      console.log('Order data to insert:', orderData);

      const { data: orderResult, error: orderError } = await supabase
        .from('orders')
        .insert(orderData)
        .select()
        .single();

      if (orderError) {
        console.error('Order creation error:', orderError);
        throw new Error(`Order creation failed: ${orderError.message}`);
      }

      console.log('Order created:', orderResult);

      // Step 4: Create order items and deduct stock
      console.log('Step 4: Creating order items and deducting stock...');
      const orderItems = cartItems.map(item => {
        // Try to get brand_id from different possible locations
        const brandId = item.brand_id || item.product?.brand_id || item.product?.brand?.id;
        
        if (!brandId) {
          console.error('Missing brand_id in cart item:', item);
          throw new Error('Cart item missing brand_id');
        }

        // Get custom fields from cart item attributes (if stored there)
        // Check localStorage as fallback (in case cart_items.attributes column doesn't exist)
        let customFieldsData = {};
        if (item.attributes && typeof item.attributes === 'object' && Object.keys(item.attributes).length > 0) {
          customFieldsData = item.attributes;
        } else {
          // Try to get from localStorage as fallback
          try {
            const storedFields = localStorage.getItem(`cart_item_custom_fields_${item.id}`);
            if (storedFields) {
              customFieldsData = JSON.parse(storedFields);
            }
          } catch (e) {
            console.warn('Failed to parse custom fields from localStorage:', e);
          }
        }
        
        // If still empty, fall back to product attributes
        if (Object.keys(customFieldsData).length === 0) {
          customFieldsData = item.product?.attributes || {};
        }
        
        // Ensure variant_id is properly handled (can be string UUID or null)
        const variantId = item.variant_id && item.variant_id !== 'null' && item.variant_id !== '' 
          ? item.variant_id 
          : null;
        
        return {
          order_id: orderResult.id,
          brand_id: brandId,
          product_id: item.product_id,
          variant_id: variantId, // Store variant_id for stock deduction and order tracking
          product_name: item.product?.name || item.product_name || 'Unknown Product',
          sku: item.product?.sku || item.sku || null,
          unit_price: Number(item.price),
          quantity: Number(item.quantity),
          attributes: customFieldsData, // Store custom fields in attributes
          note: item.note || null
        };
      });

      console.log('Order items to insert:', orderItems);
      console.log('Order items variant_ids check:', orderItems.map(oi => ({ 
        product_id: oi.product_id, 
        variant_id: oi.variant_id,
        has_variant: !!oi.variant_id 
      })));

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) {
        console.error('Order items error:', itemsError);
        throw new Error(`Order items creation failed: ${itemsError.message}`);
      }

      // Clean up localStorage for custom fields (since order is created successfully)
      cartItems.forEach(item => {
        try {
          localStorage.removeItem(`cart_item_custom_fields_${item.id}`);
        } catch (e) {
          console.warn('Failed to cleanup localStorage for cart item:', e);
        }
      });

      // Step 5: Deduct stock from variants and products
      console.log('Step 5: Deducting stock...');
      for (const item of cartItems) {
        const quantity = Number(item.quantity);
        
        if (item.variant_id) {
          // Deduct from variant stock first
          const { data: variantData, error: variantFetchError } = await supabase
            .from('product_variants')
            .select('stock, product_id')
            .eq('id', item.variant_id)
            .single();

          if (!variantFetchError && variantData) {
            const newVariantStock = Math.max(0, variantData.stock - quantity);
            const { error: variantStockError } = await supabase
              .from('product_variants')
              .update({ stock: newVariantStock })
              .eq('id', item.variant_id);

            if (variantStockError) {
              console.error('Error deducting variant stock:', variantStockError);
            } else {
              console.log(`✅ Deducted ${quantity} from variant ${item.variant_id} (${variantData.stock} → ${newVariantStock})`);
            }

            // Also deduct from product stock
            const { data: productData, error: productFetchError } = await supabase
              .from('products')
              .select('stock')
              .eq('id', item.product_id)
              .single();

            if (!productFetchError && productData) {
              const newProductStock = Math.max(0, productData.stock - quantity);
              const { error: productStockError } = await supabase
                .from('products')
                .update({ stock: newProductStock })
                .eq('id', item.product_id);

              if (productStockError) {
                console.error('Error deducting product stock:', productStockError);
              } else {
                console.log(`✅ Deducted ${quantity} from product ${item.product_id} (${productData.stock} → ${newProductStock})`);
              }
            }
          }
        } else {
          // No variant - only deduct from product stock
          const { data: productData, error: productFetchError } = await supabase
            .from('products')
            .select('stock')
            .eq('id', item.product_id)
            .single();

          if (!productFetchError && productData) {
            const newProductStock = Math.max(0, productData.stock - quantity);
            const { error: productStockError } = await supabase
              .from('products')
              .update({ stock: newProductStock })
              .eq('id', item.product_id);

            if (productStockError) {
              console.error('Error deducting product stock:', productStockError);
            } else {
              console.log(`✅ Deducted ${quantity} from product ${item.product_id} (${productData.stock} → ${newProductStock})`);
            }
          }
        }
      }

      // Step 6: Clear cart items after order creation
      // This prevents purchased items (and their notes/custom fields) from remaining in cart.
      try {
        const { error: clearCartError } = await supabase
          .from('cart_items')
          .delete()
          .eq('cart_id', cart.id);

        if (clearCartError) {
          console.error('⚠️ Failed to clear cart items after order creation:', clearCartError);
        } else {
          window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { count: 0, cart: null, items: [] } }));
          console.log('✅ Cart cleared after order creation');
        }
      } catch (clearErr) {
        console.error('⚠️ Error clearing cart after order creation:', clearErr);
      }

      console.log('✅ Order created successfully with ID:', orderResult.id);
      console.log('📋 Order #', orderResult.order_number, 'status:', orderResult.status, 'Payment status:', orderResult.payment_status);
      console.log('📧 Order confirmation emails will be sent after payment is confirmed');

      return { id: orderResult.id, order_number: orderResult.order_number };

    } catch (err) {
      console.error('Error creating order:', err);
      throw err;
    }
  };

  /**
   * Customer + store owner emails via Node email-server (same as Admin Status shipment mail).
   * Returns human-readable log lines for the order confirmation UI.
   */
  const sendPaidOrderNotificationEmails = useCallback(async (orderId, options = {}) => {
    const { waitForPaidRow = false } = options;
    const logs = [];
    const log = (line) => {
      const entry = `[${new Date().toLocaleTimeString()}] ${line}`;
      logs.push(entry);
      if (process.env.NODE_ENV === 'development') {
        console.log(entry);
      }
    };

    const orderSelect = () =>
      supabase
        .from('orders')
        .select(`
            *,
            order_items:order_items(
              *,
              products:products(id, name, images),
              brand:brands(id, name, created_by)
            ),
            shipping_information:shipping_information(*)
          `)
        .eq('id', orderId)
        .single();

    try {
      let resolvedUrl = '';
      try {
        resolvedUrl = getResolvedEmailApiUrl();
      } catch (_e) {
        resolvedUrl = '(could not resolve)';
      }
      log(`Email API URL: ${resolvedUrl}`);
      log(`REACT_APP_EMAIL_API_ENDPOINT (build): ${process.env.REACT_APP_EMAIL_API_ENDPOINT || '(not set — dev: localhost → /api proxy; LAN IP → direct :REACT_APP_EMAIL_SERVER_PORT or 3002)'}`);

      const maxAttempts = waitForPaidRow ? 12 : 1;
      const delayMs = 280;
      let orderData = null;
      let orderFetchError = null;

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        // eslint-disable-next-line no-await-in-loop
        const res = await orderSelect();
        orderData = res.data;
        orderFetchError = res.error;

        if (orderFetchError || !orderData) {
          break;
        }
        if (orderData.payment_status === 'paid') {
          if (waitForPaidRow && attempt > 1) {
            log(`Order row showed paid on attempt ${attempt} (after mark_order_paid).`);
          }
          break;
        }
        if (attempt < maxAttempts) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, delayMs));
        }
      }

      if (orderFetchError || !orderData) {
        log(`ERROR: Could not load order — ${orderFetchError?.message || 'no row'}`);
        return { ok: false, logs };
      }

      log(
        `Order #${formatOrderReference(orderData)} loaded (id=${orderId}); payment_status=${orderData.payment_status}`
      );

      if (orderData.payment_status !== 'paid') {
        log(
          waitForPaidRow
            ? 'SKIP: payment_status still not "paid" after retries — emails not sent (check RLS/replica lag).'
            : 'SKIP: Emails only send when payment_status is "paid".'
        );
        return { ok: false, logs };
      }

      const { data: { user } } = await supabase.auth.getUser();
      const shippingData = orderData.shipping_information || {};
      let profileEmail = null;
      try {
        const { data: rpcEmail, error: rpcErr } = await supabase.rpc('get_user_email', {
          user_id: orderData.profile_id,
        });
        if (!rpcErr && typeof rpcEmail === 'string' && rpcEmail.includes('@')) {
          profileEmail = rpcEmail.trim();
        }
      } catch (_e) {
        // ignore
      }
      const shippingEmail =
        shippingData?.email && String(shippingData.email).includes('@')
          ? String(shippingData.email).trim()
          : null;
      const customerEmail = profileEmail || user?.email || shippingEmail || null;
      const customerName = shippingData?.full_name || user?.user_metadata?.full_name || 'Customer';

      log(`Customer email resolved: ${customerEmail || '(none — profile / session / shipping)'}`);
      log(`Sources: profile RPC=${profileEmail || '—'}, session=${user?.email || '—'}, shipping=${shippingEmail || '—'}`);

      const shippingAddressForEmail = {
        ...shippingData,
        email: customerEmail || null,
      };

      const orderItemsData = orderData.order_items || [];
      const emailItems = orderItemsData.map((item) => ({
        name: item.products?.name || item.product_name || 'Unknown Product',
        quantity: item.quantity || 1,
        price: parseFloat(item.unit_price || item.price || 0).toFixed(2),
      }));

      const emailOrderRef =
        orderData.order_number != null ? String(orderData.order_number) : String(orderData.id);

      if (customerEmail) {
        log(`Sending customer confirmation → ${customerEmail}`);
        try {
          const result = await sendOrderConfirmation({
            customerEmail,
            customerName,
            orderId: emailOrderRef,
            orderTotal: parseFloat(orderData.grand_total || 0).toFixed(2),
            items: emailItems,
            orderDate: new Date(orderData.created_at).toLocaleDateString(),
          });
          if (result?.success) {
            log(`OK: Customer email sent (messageId=${result.messageId || 'n/a'})`);
          } else {
            log(`FAIL: Customer email — ${result?.error || 'unknown'} (endpoint=${result?.endpoint || 'n/a'})`);
          }
        } catch (err) {
          log(`FAIL: Customer email exception — ${err?.message || err}`);
        }
      } else {
        log('SKIP: No customer email — cannot send confirmation.');
      }

      const brandIds = [...new Set(orderItemsData?.map((item) => item.brand_id || item.brand?.id).filter(Boolean))];
      log(`Brand IDs for store notifications: ${brandIds.length ? brandIds.join(', ') : '(none)'}`);

      const fetchEmailsForProfiles = async (profileIds) => {
        const uniqueIds = [...new Set((profileIds || []).filter(Boolean))];
        const emails = new Map();
        const BATCH = 12;
        for (let i = 0; i < uniqueIds.length; i += BATCH) {
          const batch = uniqueIds.slice(i, i + BATCH);
          // eslint-disable-next-line no-await-in-loop
          const results = await Promise.all(
            batch.map(async (profileId) => {
              try {
                const { data: email, error: rpcError } = await supabase.rpc('get_user_email', {
                  user_id: profileId,
                });
                if (!rpcError && email && typeof email === 'string') {
                  return { profileId, email: email.trim() };
                }
              } catch (_e) {
                // ignore
              }
              return null;
            })
          );
          results.forEach((r) => {
            if (r?.email && r.email.includes('@')) emails.set(r.profileId, r.email);
          });
        }
        return emails;
      };

      for (const brandId of brandIds) {
        try {
          const brandData = orderItemsData.find((item) => (item.brand_id || item.brand?.id) === brandId)?.brand;
          if (!brandData) {
            log(`Brand ${brandId}: skip (no brand row on items)`);
            continue;
          }

          const { data: managers, error: managersError } = await supabase
            .from('brand_managers')
            .select('profile_id')
            .eq('brand_id', brandId);

          if (managersError) {
            log(`WARN: brand_managers for ${brandId}: ${managersError.message}`);
          }

          const managerProfileIds = (managers || []).map((m) => m.profile_id).filter(Boolean);
          const recipientProfileIds = [...managerProfileIds, brandData.created_by].filter(Boolean);

          const profileEmailMap = await fetchEmailsForProfiles(recipientProfileIds);
          const recipientEmails = [...new Set([...profileEmailMap.values()].filter(Boolean))];

          if (recipientEmails.length === 0) {
            log(`Brand "${brandData.name || brandId}": no store recipient emails`);
            continue;
          }

          log(`Brand "${brandData.name || brandId}": sending to ${recipientEmails.join(', ')}`);

          const brandItems = orderItemsData
            .filter((item) => (item.brand_id || item.brand?.id) === brandId)
            .map((item) => ({
              name: item.products?.name || item.product_name || 'Unknown Product',
              quantity: item.quantity || 1,
              price: parseFloat(item.unit_price || item.price || 0).toFixed(2),
            }));

          const brandTotal = brandItems.reduce((sum, item) => sum + parseFloat(item.price) * item.quantity, 0);

          // eslint-disable-next-line no-await-in-loop
          const storeEmailResults = await Promise.allSettled(
            recipientEmails.map((storeEmail) =>
              sendNewOrderNotificationToStore({
                storeEmail,
                storeName: brandData.name || 'Store Owner',
                orderId: emailOrderRef,
                customerName,
                customerEmail: customerEmail || 'No email provided',
                orderTotal: brandTotal.toFixed(2),
                items: brandItems,
                shippingAddress: shippingAddressForEmail,
                orderDate: new Date(orderData.created_at).toLocaleDateString(),
              })
            )
          );

          storeEmailResults.forEach((r, idx) => {
            const storeEmail = recipientEmails[idx];
            if (r.status === 'fulfilled') {
              if (r.value?.success) {
                log(`OK: Store email → ${storeEmail}`);
              } else {
                log(`FAIL: Store email → ${storeEmail} — ${r.value?.error || 'unknown'}`);
              }
            } else {
              log(`FAIL: Store email → ${storeEmail} — ${r.reason?.message || r.reason}`);
            }
          });
        } catch (brandError) {
          log(`ERROR: brand ${brandId} — ${brandError?.message || brandError}`);
        }
      }

      log('Done.');
      return { ok: true, logs };
    } catch (emailError) {
      log(`ERROR: ${emailError?.message || emailError}`);
      return { ok: false, logs };
    }
  }, []);

  const markOrderPaid = async (orderId, paymentReference, onProgress) => {
    try {
      // Note: after returning from external payment pages, the auth context may not be hydrated yet.
      // Confirmation emails are sent from /order-confirmation (not here) so fetch() hits the email API reliably.
      console.log('Marking order as paid:', orderId, paymentReference);

      onProgress?.('recording');

      const { error: updateError } = await supabase.rpc('mark_order_paid', {
        p_order_id: orderId,
        p_reference: paymentReference
      });

      console.log('Mark order paid response - error:', updateError);

      if (updateError) {
        throw updateError;
      }

      // Confirmation emails run on /order-confirmation (reliable browser context for fetch → email API).

      // Refresh orders list
      if (session?.user?.id) {
        const { data: newData } = await supabase
          .from('orders')
          .select(`
            *,
            order_items:order_items(
              *,
              product:products(id, name, img, images)
            )
          `)
          .eq('profile_id', session.user.id)
          .eq('payment_status', 'paid')
          .order('created_at', { ascending: false });

        setOrders((newData || []).map(mapOrderWithShipmentMeta));
      } else {
        console.warn('⚠️ Skipping orders refresh (no session yet).');
      }

      return true;
    } catch (err) {
      console.error('Error marking order as paid:', err);
      throw err;
    }
  };

  return {
    orders,
    loading,
    error,
    createOrderFromCart,
    markOrderPaid,
    sendPaidOrderNotificationEmails,
    /** Same as sendPaidOrderNotificationEmails — resend customer + store emails for a paid order */
    resendOrderConfirmationEmails: sendPaidOrderNotificationEmails,
  };
};
